import { currentEditor, load } from "scrivito"
import { fetchWithRetries } from "shared/api/fetchWithRetries"
import { generateUUID } from "shared/lib/generateUUID"
import { RequestError } from "shared/types/error/RequestError"
import { UnauthorizedError } from "shared/types/error/UnauthorizedError"

import Honeybadger from "@honeybadger-io/js"

function handleError(error: unknown, context: string) {
  if (error instanceof RequestError) {
    error.notify()
  } else if (error instanceof Error) {
    Honeybadger.notify(error)
  } else {
    Honeybadger.notify(new Error(`Unknown error in ${context}`))
  }
}

async function fetchEditorToken(): Promise<string | null> {
  const currentEditorUser = await load(() => currentEditor())

  if (currentEditorUser) {
    const token: string = await load(() =>
      (currentEditorUser as unknown as { authToken: () => string }).authToken()
    )
    if (token) {
      return token
    }
  }
  return null
}

export enum APIClientEventType {
  FAILED_TO_FETCH_TOKEN = "FAILED_TO_FETCH_TOKEN"
}

export class APIClient {
  audience: string

  token: string | null | undefined

  tokenExpiry: number | null | undefined

  refreshTimeout: NodeJS.Timeout | null | undefined

  tokenPromise: Promise<string | null> | undefined

  subscriptions: [string, (type: APIClientEventType) => void][] = []

  constructor({ audience }: { audience: string }) {
    this.audience = audience
    this.token = undefined
    this.tokenPromise = undefined
  }

  async getToken() {
    if (this.token === undefined) {
      return this.fetchToken()
    }
    return this.token
  }

  private clearTokenRefreshTimeout() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
      this.refreshTimeout = null
    }
  }

  private scheduleTokenRefresh() {
    this.clearTokenRefreshTimeout()
    const timeUntilRefresh = 30 // seconds

    this.refreshTimeout = setTimeout(() => {
      this.fetchToken()
    }, timeUntilRefresh * 1000)
  }

  private async fetchVisitorToken() {
    return fetchWithRetries(
      `${window.location.origin}/auth/instance/${
        import.meta.env.VITE_SCRIVITO_TENANT
      }/token?audience=${encodeURIComponent(this.audience)}`,
      { credentials: "include" }
    )
      .then(async response => {
        return response.json().then(({ access_token }) => {
          if (access_token && typeof access_token === "string") {
            return access_token
          }
          return null
        })
      })
      .catch(error => {
        if (!(error instanceof UnauthorizedError)) {
          handleError(error, "APIClient.fetchVisitorToken")
        }
        return null
      })
  }

  async fetchToken(): Promise<string | null> {
    if (this.token === null) {
      return null
    }
    if (this.tokenPromise) {
      return this.tokenPromise
    }
    this.tokenPromise = fetchEditorToken().then(
      token => token || this.fetchVisitorToken()
    )
    this.token = await this.tokenPromise
    this.tokenPromise = undefined

    if (this.token === null) {
      this.clearTokenRefreshTimeout()
      this.notifySubscribers(APIClientEventType.FAILED_TO_FETCH_TOKEN)
    }
    if (this.token) {
      this.scheduleTokenRefresh()
    }

    return this.token
  }

  async request(path: RequestInfo | URL, init?: RequestInit) {
    try {
      return await fetchWithRetries(path, init, {
        getToken: () => this.getToken(),
        getFreshToken: () => this.fetchToken()
      })
    } catch (error) {
      handleError(error, "APIClient.request")
      throw error
    }
  }

  private unsubscribe(id: string) {
    this.subscriptions = this.subscriptions.filter(
      subscription => subscription[0] !== id
    )
  }

  subscribe(callback: (type: APIClientEventType) => void) {
    const id = generateUUID()
    this.subscriptions.push([id, callback])

    return () => this.unsubscribe(id)
  }

  private notifySubscribers(type: APIClientEventType) {
    this.subscriptions.forEach(([, callback]) => callback(type))
  }
}
